import * as React from 'react';
import { SlideSelector } from '@/components/ui/slide-selector';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { TimezoneSelect } from '@/components/ui/timezone-select';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import { Id } from '@/convex/_generated/dataModel';
import { Pod } from '@/types/pods';

interface Schedule {
	targetId: Id<'engagementPods'>;
	type: string;
	status: string;
}

interface ScheduleFormProps {
	children?: React.ReactNode;
	activeTab: string;
	onTabChange: (value: string) => void;
	frequency: string;
	onFrequencyChange: (value: string) => void;
	selectedDays: string[];
	onDaysChange: (days: string[]) => void;
	times: string[];
	onTimesChange: (times: string[]) => void;
	timezone: string;
	onTimezoneChange: (timezone: string) => void;
	randomRange: { minDelay: number; maxDelay: number };
	onRandomRangeChange: (range: {
		minDelay: number;
		maxDelay: number;
	}) => void;
	isTestMode: boolean;
	onTestModeChange: (value: boolean) => void;
	onSubmit: () => void;
	isAdmin: boolean;
	actionCount: {
		value: number | 'ALL' | 'ONE';
		max?: number;
	};
	onActionCountChange: (value: number | 'ALL' | 'ONE') => void;
	schedules: Schedule[] | undefined;
	selectedPodId: Id<'engagementPods'> | null;
	selectedPod: Pod | null;
	isCapabilityDisabled?: boolean;
	capabilityDisabledReason?: string;
}

export function ScheduleForm({
	activeTab,
	onTabChange,
	frequency,
	onFrequencyChange,
	selectedDays,
	onDaysChange,
	times,
	onTimesChange,
	timezone,
	onTimezoneChange,
	randomRange,
	onRandomRangeChange,
	isTestMode,
	onTestModeChange,
	onSubmit,
	isAdmin,
	children,
	actionCount,
	onActionCountChange,
	schedules,
	selectedPodId,
	selectedPod,
	isCapabilityDisabled,
	capabilityDisabledReason,
}: ScheduleFormProps) {
	React.useEffect(() => {
		console.log('ScheduleForm props:', {
			selectedPod,
			selectedPodId,
			schedules,
		});
	}, [selectedPod, selectedPodId, schedules]);

	const [showDuplicateWarning, setShowDuplicateWarning] =
		React.useState(false);

	const handleAddTime = () => {
		const now = new Date();
		now.setHours(now.getHours() + 1, 0, 0, 0);
		const timeStr = now.toLocaleTimeString('en-US', {
			hour: '2-digit',
			minute: '2-digit',
			hour12: false,
		});
		onTimesChange([...times, timeStr]);
	};

	const renderActionCountControl = () => {
		if (activeTab === 'scraper') {
			return (
				<div className='flex flex-col gap-2'>
					<label className='text-sm font-medium'>
						Posts to Import (per profile)
					</label>
					<Select
						value={String(actionCount.value)}
						onValueChange={(v) => {
							const value = Number(v);
							onActionCountChange(
								isNaN(value) ? (v as 'ALL' | 'ONE') : value
							);
						}}
					>
						<SelectTrigger className='w-40'>
							<SelectValue placeholder='Select count' />
						</SelectTrigger>
						<SelectContent>
							{Array.from(
								{ length: 10 },
								(_, i) => (i + 1) * 50
							).map((count) => (
								<SelectItem key={count} value={String(count)}>
									{count} posts
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			);
		}

		// For Create Comments and Publish Comments
		return (
			<div className='flex flex-col gap-2'>
				<label className='text-sm font-medium'>
					{activeTab === 'pod'
						? 'Comments to Generate'
						: 'Comments to Publish'}
				</label>
				<div className='flex items-center gap-2'>
					<Select
						value={
							actionCount.value === 'ALL' ||
							actionCount.value === 'ONE'
								? String(actionCount.value)
								: 'custom'
						}
						onValueChange={(v) =>
							onActionCountChange(
								v === 'custom'
									? 1
									: v === 'ALL' || v === 'ONE'
										? v
										: Number(v)
							)
						}
					>
						<SelectTrigger className='w-56'>
							<SelectValue
								placeholder='Select count'
								className='whitespace-nowrap'
							>
								{actionCount.value === 'ALL'
									? 'All Available'
									: actionCount.value === 'ONE'
										? 'One Comment'
										: `Custom Number (${actionCount.value})`}
							</SelectValue>
						</SelectTrigger>
						<SelectContent>
							<SelectItem value='ALL'>All Available</SelectItem>
							<SelectItem value='ONE'>One Comment</SelectItem>
							<SelectItem value='custom'>
								Custom Number
							</SelectItem>
						</SelectContent>
					</Select>

					{actionCount.value !== 'ALL' &&
						actionCount.value !== 'ONE' && (
							<Input
								type='number'
								min={1}
								value={
									typeof actionCount.value === 'number'
										? String(actionCount.value)
										: '1'
								}
								onChange={(e) =>
									onActionCountChange(Number(e.target.value))
								}
								className='w-24'
							/>
						)}
				</div>
			</div>
		);
	};

	const checkForDuplicateSchedule = (type: string) => {
		console.log('Checking duplicates:', { type, schedules, selectedPodId });
		if (!schedules) return false;
		return schedules.some((s) => {
			console.log('Checking schedule:', s);
			return (
				s.targetId === selectedPodId &&
				s.type === type &&
				s.status === 'active'
			);
		});
	};

	const handleSubmit = async () => {
		console.log('Submit clicked:', {
			activeTab,
			selectedPod,
			schedules,
		});

		try {
			if (!selectedPod) {
				console.log('No pod selected');
				toast({
					title: 'Error',
					description: 'No pod selected',
					variant: 'destructive',
				});
				return;
			}
			if (isCapabilityDisabled) {
				toast({
					title: 'Schedule Disabled',
					description:
						capabilityDisabledReason ||
						'This pod mode is disabled for the current Upload Post capability matrix.',
					variant: 'destructive',
				});
				return;
			}

			// Map activeTab to correct schedule type
			const scheduleType =
				activeTab === 'comments'
					? 'comments'
					: activeTab === 'scraper'
						? 'post_scraper'
						: activeTab === 'pod'
							? 'pod_start'
							: null;

			console.log('Checking for duplicates:', {
				activeTab,
				scheduleType,
			});

			// Check for duplicate schedules using correct type
			if (scheduleType && checkForDuplicateSchedule(scheduleType)) {
				console.log('Duplicate found');
				const scheduleMessages = {
					comments: {
						title: '📣 Duplicate Publishing Schedule',
						description:
							'This pod already has an active PUBLISH COMMENTS schedule running. Please pause or delete the existing schedule first.',
					},
					post_scraper: {
						title: '⚡️ Duplicate Source Import Schedule',
						description:
							'This pod already has an active SOURCE IMPORT schedule running. Please pause or delete the existing schedule first.',
					},
					pod_start: {
						title: '✨ Duplicate Generation Schedule',
						description:
							'This pod already has an active COMMENTS GENERATION schedule running. Please pause or delete the existing schedule first.',
					},
				};

				const message =
					scheduleMessages[
						scheduleType as keyof typeof scheduleMessages
					];
				toast({
					title: message.title,
					description: message.description,
					variant: 'destructive',
					duration: 3000,
				});
				return;
			}

			console.log('No duplicates found, proceeding with submit');
			onSubmit();
		} catch (error) {
			console.error('Submit error:', error);
			toast({
				title: 'Error',
				description:
					error instanceof Error ? error.message : 'Unknown error',
				variant: 'destructive',
			});
		}
	};

	return (
		<div className='space-y-6'>
			{isCapabilityDisabled && (
				<div className='rounded-md border border-orange-300 bg-orange-50 p-4 text-sm text-orange-900 dark:border-orange-900/60 dark:bg-orange-950/40 dark:text-orange-200'>
					{capabilityDisabledReason}
				</div>
			)}
			<SlideSelector
				value={activeTab}
				onChange={onTabChange}
				options={[
					{
						value: 'scraper',
						label: 'Source Import',
						icon: '⚡️',
						description: 'Schedule Target Collection',
						className:
							'bg-gradient-to-b from-pink-300 via-pink-500 to-pink-700',
						labelClassName: '-mt-5',
						descriptionClassName: '-mt-5',
					},
					{
						value: 'pod',
						label: 'Create Comments',
						icon: '✨',
						description: 'Schedule Comments Generation',
						className:
							'bg-gradient-to-b from-purple-300 via-purple-500 to-purple-800',
						labelClassName: '-mt-5',
						descriptionClassName: '-mt-5',
					},
					{
						value: 'comments',
						label: 'Publish Comments',
						icon: '📣',
						description: 'Schedule Comments Publishing',
						className:
							'bg-gradient-to-b from-blue-300 via-blue-500 to-blue-700',
						labelClassName: '-mt-5',
						descriptionClassName: '-mt-5',
					},
				]}
				className='w-full mb-6'
				activeColor={
					activeTab === 'pod'
						? 'purple'
						: activeTab === 'scraper'
							? 'pink'
							: 'blue'
				}
			/>

			<Card className='border-2 border-purple-500/20'>
				<CardContent className='pt-6 space-y-4'>
					<Select value={frequency} onValueChange={onFrequencyChange}>
						<SelectTrigger>
							<SelectValue placeholder='Select frequency' />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value='daily'>Daily</SelectItem>
							<SelectItem value='weekly'>Weekly</SelectItem>
							<SelectItem value='random'>Random</SelectItem>
						</SelectContent>
					</Select>

					{frequency === 'weekly' && (
						<div className='space-y-2'>
							<p className='text-sm font-medium'>Select Days</p>
							<div className='flex gap-2 flex-wrap'>
								{[
									'Monday',
									'Tuesday',
									'Wednesday',
									'Thursday',
									'Friday',
									'Saturday',
									'Sunday',
								].map((day) => (
									<div
										key={day}
										className='flex items-center space-x-2'
									>
										<Checkbox
											checked={selectedDays.includes(
												day.toLowerCase()
											)}
											onCheckedChange={(checked) => {
												if (checked) {
													onDaysChange([
														...selectedDays,
														day.toLowerCase(),
													]);
												} else {
													onDaysChange(
														selectedDays.filter(
															(d) =>
																d !==
																day.toLowerCase()
														)
													);
												}
											}}
										/>
										<label className='text-sm'>{day}</label>
									</div>
								))}
							</div>
						</div>
					)}

					{frequency === 'random' && (
						<div className='space-y-2'>
							<p className='text-sm font-medium'>
								Random Delay Range (minutes)
							</p>
							<div className='flex gap-2'>
								<Input
									type='number'
									value={randomRange.minDelay}
									onChange={(e) =>
										onRandomRangeChange({
											...randomRange,
											minDelay: parseInt(e.target.value),
										})
									}
									placeholder='Min delay'
								/>
								<Input
									type='number'
									value={randomRange.maxDelay}
									onChange={(e) =>
										onRandomRangeChange({
											...randomRange,
											maxDelay: parseInt(e.target.value),
										})
									}
									placeholder='Max delay'
								/>
							</div>
						</div>
					)}

					{frequency !== 'random' && (
						<div className='space-y-2'>
							<div className='flex items-center justify-between'>
								<p className='text-sm font-medium'>Times</p>
								<Button
									variant='outline'
									size='sm'
									onClick={handleAddTime}
								>
									<span className='flex items-center gap-1'>
										⏰ <span>Add Time</span>
									</span>
								</Button>
							</div>
							{times.map((time, index) => (
								<div
									key={index}
									className='flex items-center gap-2'
								>
									<Input
										type='time'
										value={time}
										onChange={(e) => {
											const newTimes = [...times];
											newTimes[index] = e.target.value;
											onTimesChange(newTimes);
										}}
										className='focus-visible:ring-purple-500 focus-visible:border-purple-500 border-purple-500/20'
									/>
									{index > 0 && (
										<Button
											variant='ghost'
											size='sm'
											onClick={() => {
												onTimesChange(
													times.filter(
														(_, i) => i !== index
													)
												);
											}}
											className='hover:bg-purple-500/20 hover:text-purple-500'
										>
											✕
										</Button>
									)}
								</div>
							))}
						</div>
					)}

					<TimezoneSelect
						value={timezone}
						onValueChange={onTimezoneChange}
					/>

					<div className='mb-4'>{renderActionCountControl()}</div>

					<div className='flex items-center justify-between'>
						{isAdmin && (
							<div className='flex items-center gap-2 p-2 bg-secondary/20 rounded-lg'>
								<Switch
									checked={isTestMode}
									onCheckedChange={onTestModeChange}
									id='test-mode'
								/>
								<label
									htmlFor='test-mode'
									className='text-sm font-medium'
								>
									Test Mode {isTestMode && '🧪'}
								</label>
								{isTestMode && (
									<span className='text-xs text-muted-foreground ml-2'>
										{activeTab === 'pod'
											? 'Will process only one comment'
											: activeTab === 'scraper'
												? 'Source import run will be simulated'
												: 'Will simulate publishing one comment'}
									</span>
								)}
							</div>
						)}

						<Button
							onClick={handleSubmit}
							disabled={isCapabilityDisabled}
							className={cn(
								'text-white h-12 w-96',
								activeTab === 'pod'
									? 'bg-purple-600 hover:bg-purple-700'
									: activeTab === 'scraper'
										? 'bg-pink-600 hover:bg-pink-700'
										: 'bg-blue-600 hover:bg-blue-700'
							)}
						>
							{children}
						</Button>
					</div>
				</CardContent>
			</Card>

			<Dialog
				open={showDuplicateWarning}
				onOpenChange={setShowDuplicateWarning}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle className='text-xl font-bold flex items-center gap-2'>
							⚠️ Duplicate Schedule Warning
						</DialogTitle>
					</DialogHeader>
					<div className='py-4'>
						<p className='text-orange-600 dark:text-orange-400'>
							This pod already has an active PUBLISH COMMENTS
							schedule.
						</p>
						<p className='mt-2 text-muted-foreground'>
							Please pause or delete the existing schedule before
							creating a new one.
						</p>
					</div>
					<div className='flex justify-end'>
						<Button
							variant='outline'
							onClick={() => setShowDuplicateWarning(false)}
						>
							Got it
						</Button>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
}
